import React from 'react';
import styled from 'styled-components';
import TrueImpactText from '../base/TrueImpactText';
import { TYPOGRAPHY } from '../settings/variables/Typography';
import { SPACERS } from '../settings/variables/Spacers';
import { COLORS } from '../settings/variables/Colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

interface TrueImpactTextIndicatorProps {
  text?: string;
  minLength: number;
  maxLength: number;
  handleClear?: () => unknown;
}

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${SPACERS.tiny};
  .requirement {
    margin: 0;
    font-style: ${TYPOGRAPHY.fontStyleItalic};
  }
  .clear-button {
    min-width: 100px;
    background: none;
    border: none;
    outline: none;
    cursor: pointer;
    text-align: left;
  }
`;

const CharacterCountWrapper = styled.div`
  min-width: 100px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${SPACERS.small};
  .counter-circle {
    display: block;
    width: 24px;
    height: 24px;
    transform: rotate(-90deg);
  }
`;

interface LengthCounterProps {
  length: number;
  maxLength: number;
  minLength: number;
}

const LengthCounter: React.FC<LengthCounterProps> = ({
  minLength,
  maxLength,
  length,
}) => {
  return (
    <CharacterCountWrapper>
      <TrueImpactText
        variant="body-small"
        align="right"
        color={
          length <= 0
            ? 'greyDark'
            : length < minLength
            ? 'critical'
            : length > maxLength
            ? 'critical'
            : 'greyDark'
        }
      >
        {length}
        <span className="minCharacter">/{maxLength}</span>
      </TrueImpactText>
      <div className="counter-circle">
        <svg
          height="100%"
          viewBox="0 0 20 20"
          width="100%"
          style={{ overflow: 'visible' }}
        >
          <circle
            cx="50%"
            cy="50%"
            fill="none"
            stroke-width="2"
            r="9"
            stroke={
              length <= 0
                ? COLORS.greyLight
                : length < minLength
                ? COLORS.criticalBackground
                : length > maxLength
                ? COLORS.criticalBackground
                : COLORS.highlightBackground
            }
          ></circle>
          <circle
            cx="50%"
            cy="50%"
            fill="none"
            stroke-width="2"
            r="9"
            stroke={
              length <= 0
                ? COLORS.greyDark
                : length < minLength
                ? COLORS.critical
                : length > maxLength
                ? COLORS.critical
                : COLORS.highlight
            }
            stroke-linecap="round"
            style={{
              strokeDashoffset:
                length < maxLength
                  ? 56.5487 - (56.5487 / maxLength) * length
                  : 0,
              strokeDasharray: 56.5487,
            }}
          ></circle>
        </svg>
      </div>
    </CharacterCountWrapper>
  );
};

const TrueImpactTextIndicator: React.FC<TrueImpactTextIndicatorProps> = ({
  text = '',
  minLength,
  maxLength,
  handleClear,
}) => {
  return (
    <StyledDiv>
      {handleClear && (
        <div className="clear-button" onClick={handleClear}>
          <FontAwesomeIcon icon={faTimes} /> Clear
        </div>
      )}
      {minLength ? (
        <TrueImpactText
          variant="body-small"
          align="center"
          color="greyDark"
          className="requirement"
        >
          At least {minLength} characters are required.
        </TrueImpactText>
      ) : (
        <div />
      )}
      <LengthCounter
        length={text.length}
        minLength={minLength}
        maxLength={maxLength}
      />
    </StyledDiv>
  );
};

export default TrueImpactTextIndicator;
